/**
 * SFrame240 — Property / vehicle / business executions. Sources sframe-155 & sframe-160. State sframe-240.
 */

import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { SettlementStepPage } from '../../settlement';
import { SETTLEMENT_PHASE_CONFIGS } from '../../../utils/settlementFlowConfig';
import { useSettlementSession } from '../../../hooks/useSettlementSession';
import { useSettlement } from '../../../contexts/SettlementContext';
import { extractBusinessFrom160, extractRealEstate155, extractVehicles155, type StreamJPropertyStatus } from './streamJSources';
import { RowDocumentPanel } from './streamJRowDocs';

const step = SETTLEMENT_PHASE_CONFIGS.distribute.steps.find((s) => s.id === 'dist-5')!;
const PST: StreamJPropertyStatus[] = ['not_started', 'listed', 'pending', 'sold', 'transferred', 'blocked'];
const PLABEL: Record<StreamJPropertyStatus, string> = {
  not_started: 'Not Started',
  listed: 'Listed',
  pending: 'Pending',
  sold: 'Sold',
  transferred: 'Transferred',
  blocked: 'Blocked',
};
type Tab = 'all' | StreamJPropertyStatus;
type SrcRow = {
  key: string;
  title: string;
  subtype: string;
  beneficiary: string;
  method: string;
  appraised?: number;
  requiresCourt: boolean;
};
type Exec240 = {
  status: StreamJPropertyStatus;
  notes: string;
  blockerReason: string;
  proceedsReceived: string;
  courtApprovalStatus: 'not_required' | 'pending' | 'approved';
};

function defaults(rc: boolean): Exec240 {
  return {
    status: 'not_started',
    notes: '',
    blockerReason: '',
    proceedsReceived: '',
    courtApprovalStatus: rc ? 'pending' : 'not_required',
  };
}

function coerce240(raw: unknown): Record<string, Exec240> {
  const out: Record<string, Exec240> = {};
  if (!raw || typeof raw !== 'object') return out;
  const ex = (raw as { executions?: unknown }).executions;
  if (!ex || typeof ex !== 'object') return out;
  for (const [id, row] of Object.entries(ex)) {
    if (!row || typeof row !== 'object') continue;
    const r = row as Record<string, unknown>;
    const st = String(r.status) as StreamJPropertyStatus;
    const cot = String(r.courtApprovalStatus ?? '');
    out[id] = {
      status: PST.indexOf(st) >= 0 ? st : 'not_started',
      notes: String(r.notes ?? ''),
      blockerReason: String(r.blockerReason ?? ''),
      proceedsReceived: String(r.proceedsReceived ?? ''),
      courtApprovalStatus: cot === 'pending' || cot === 'approved' ? cot : 'not_required',
    };
  }
  return out;
}

function moneyFmt(n: number): string {
  if (!Number.isFinite(n)) return '—';
  return n.toLocaleString(undefined, { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });
}

export default function SFrame240() {
  const { estateId: param } = useParams<{ estateId: string }>();
  const estateId = param ?? null;
  const { isArchived } = useSettlement();
  const { loadFrameData, saveFrameData } = useSettlementSession(estateId, { readOnly: isArchived });

  const [src, setSrc] = useState<SrcRow[]>([]);
  const [exec, setExec] = useState<Record<string, Exec240>>({});
  const [tab, setTab] = useState<Tab>('all');
  const [ready, setReady] = useState(false);

  useEffect(() => {
    setReady(false);
    if (!estateId) {
      setSrc([]);
      setExec({});
      setReady(true);
      return;
    }
    let cancelled = false;
    (async () => {
      const [inv160, inv155, persisted] = await Promise.all([
        loadFrameData('sframe-160'),
        loadFrameData('sframe-155'),
        loadFrameData('sframe-240'),
      ]);
      const d160 = (inv160?.data as Record<string, unknown>) ?? null;
      const d155 = (inv155?.data as Record<string, unknown>) ?? null;
      const list: SrcRow[] = [];
      extractRealEstate155(d155).forEach((r) =>
        list.push({
          key: `re:${r.id}`,
          title: r.address,
          subtype: 'Real estate',
          beneficiary: r.beneficiaryDisplay,
          method: r.distributionMethod,
          appraised: r.appraisedValue,
          requiresCourt: r.requiresCourt,
        })
      );
      extractVehicles155(d155).forEach((v) =>
        list.push({
          key: `veh:${v.id}`,
          title: v.description,
          subtype: 'Vehicle',
          beneficiary: v.beneficiaryDisplay,
          method: v.distributionMethod,
          appraised: v.appraisedValue,
          requiresCourt: false,
        })
      );
      extractBusinessFrom160(d160).forEach((b) =>
        list.push({
          key: `bz:${b.id}`,
          title: b.name,
          subtype: 'Business interest',
          beneficiary: b.beneficiaryDisplay,
          method: b.distributionMethod,
          appraised: b.appraisedValue,
          requiresCourt: b.requiresCourt,
        })
      );
      const persistedExec = coerce240(persisted?.data);
      const merged: Record<string, Exec240> = { ...persistedExec };
      for (const row of list) merged[row.key] = merged[row.key] ?? defaults(row.requiresCourt);
      if (!cancelled) {
        setExec(merged);
        setSrc(list);
        setReady(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [estateId, loadFrameData]);

  const gx = useCallback(
    (k: string, rc: boolean): Exec240 => exec[k] ?? defaults(rc),
    [exec]
  );

  const patch = useCallback(
    (k: string, rc: boolean, p: Partial<Exec240>) => {
      if (isArchived) return;
      setExec((prev) => {
        const base = prev[k] ?? defaults(rc);
        const next = { ...base, ...p };
        if (!rc) next.courtApprovalStatus = 'not_required';
        if (next.status !== 'sold') next.proceedsReceived = '';
        return { ...prev, [k]: next };
      });
    },
    [isArchived]
  );

  useEffect(() => {
    if (!estateId || isArchived || !ready) return;
    const tm = window.setTimeout(
      () => void saveFrameData('sframe-240', { version: 1, executions: exec } as Record<string, unknown>),
      520
    );
    return () => clearTimeout(tm);
  }, [exec, estateId, isArchived, ready, saveFrameData]);

  const filtered = useMemo(() => {
    if (tab === 'all') return src;
    return src.filter((row) => gx(row.key, row.requiresCourt).status === tab);
  }, [src, tab, gx]);

  const progress = useMemo(() => {
    const n = src.length;
    const done = src.filter((r) => gx(r.key, r.requiresCourt).status === 'transferred').length;
    return { n, done, pct: n ? Math.round((done / n) * 100) : 0 };
  }, [src, gx]);

  const totals = useMemo(() => {
    let appraise = 0;
    let proceeds = 0;
    let awaiting = 0;
    for (const r of src) {
      if (typeof r.appraised === 'number' && Number.isFinite(r.appraised)) appraise += r.appraised;
      const e = gx(r.key, r.requiresCourt);
      const p = Number(String(e.proceedsReceived).replace(/[^0-9.-]/g, ''));
      if (Number.isFinite(p)) proceeds += p;
      if (e.status !== 'transferred' && e.status !== 'blocked') awaiting += 1;
    }
    return { appraise, proceeds, awaiting };
  }, [src, gx]);

  const everyTransferred =
    src.length > 0 && src.every((r) => gx(r.key, r.requiresCourt).status === 'transferred');
  const empty = ready && src.length === 0;
  const ei = estateId ?? '';

  return (
    <SettlementStepPage
      step={step}
      phase="distribute"
      overviewRoute="/sframe-220"
      plannerFrameIds={['frame-246']}
      settlementDocumentContext={{ phase: 'distribute', stepId: 'dist-5' }}
    >
      <>
        <div className="space-y-4 mb-40">
          <p className="text-sm text-stone-700 max-w-3xl">
            Executes titled assets surfaced from <code className="text-xs">sframe-155</code> alongside business disclosures from{' '}
            <code className="text-xs">sframe-160</code>. Persisted payloads live on <code className="text-xs">sframe-240</code>.
          </p>
          {!empty && (
            <>
              <p className="text-xs uppercase tracking-wide text-stone-600">Transfers completed</p>
              <div className="h-2 max-w-xl rounded-full bg-stone-200 overflow-hidden">
                <div className="h-full bg-blue-900" style={{ width: `${progress.pct}%` }} />
              </div>
              <p className="text-sm">{progress.done} of {progress.n} labeled transferred</p>
            </>
          )}
          <div className="flex flex-wrap gap-2">
            {(['all'] as Tab[]).concat(PST).map((k) => (
              <button
                key={String(k)}
                type="button"
                onClick={() => setTab(k)}
                className={`px-3 py-1.5 rounded-full text-xs border font-semibold ${tab === k ? 'bg-blue-900 text-white border-blue-950' : 'border-stone-300 bg-white'}`}
              >
                {k === 'all' ? 'All' : PLABEL[k]}
              </button>
            ))}
          </div>

          {empty ? (
            <div className="rounded-xl border border-amber-200 bg-amber-50 p-6 text-sm space-y-3">
              <p className="font-semibold">No property, vehicle or business rows yet.</p>
              {ei ? (
                <div className="flex flex-wrap gap-4 font-semibold text-blue-900">
                  <Link className="underline" to={`/sframe-155/${ei}`}>Step 155 (real estate &amp; vehicles)</Link>
                  <Link className="underline" to={`/sframe-160/${ei}`}>Step 160 (business)</Link>
                </div>
              ) : null}
            </div>
          ) : (
            <div className="overflow-x-auto rounded-xl border border-stone-200 pb-24">
              <table className="min-w-[900px] text-sm">
                <thead className="bg-stone-100 text-[11px] uppercase tracking-wide">
                  <tr>
                    <th className="py-2 px-2 text-left">Asset</th>
                    <th className="py-2 px-2 hidden sm:table-cell text-left">Type</th>
                    <th className="py-2 px-2 hidden md:table-cell text-left">Beneficiary</th>
                    <th className="py-2 px-2 hidden lg:table-cell text-left">Plan method</th>
                    <th className="py-2 px-2 hidden lg:table-cell text-left">Appraised</th>
                    <th className="py-2 px-2 text-left">Status</th>
                    <th className="py-2 px-2 hidden xl:table-cell text-left">Proceeds</th>
                    <th className="py-2 px-2 hidden xl:table-cell text-left">Court</th>
                    <th className="py-2 px-2 text-left">Notes</th>
                    <th className="py-2 px-2 text-left">Upload</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((r) => {
                    const x = gx(r.key, r.requiresCourt);
                    return (
                      <Fragment key={r.key}>
                        <tr className="border-b border-stone-100 hover:bg-stone-50">
                          <td className="py-2 px-2">{r.title}</td>
                          <td className="py-2 px-2 hidden sm:table-cell">{r.subtype}</td>
                          <td className="py-2 px-2 hidden md:table-cell">{r.beneficiary}</td>
                          <td className="py-2 px-2 hidden lg:table-cell">{r.method}</td>
                          <td className="py-2 px-2 hidden lg:table-cell">{moneyFmt(r.appraised ?? NaN)}</td>
                          <td className="py-2 px-2">
                            <select
                              className="text-xs border border-stone-200 rounded px-1 py-1 bg-white"
                              disabled={!estateId || isArchived}
                              value={x.status}
                              onChange={(ev) => patch(r.key, r.requiresCourt, { status: ev.target.value as StreamJPropertyStatus })}
                            >
                              {PST.map((s) => (
                                <option key={s} value={s}>{PLABEL[s]}</option>
                              ))}
                            </select>
                          </td>
                          <td className="py-2 px-2 hidden xl:table-cell align-top">
                            <input
                              type="number"
                              className="w-28 rounded border px-2 py-1 text-xs disabled:opacity-40"
                              disabled={x.status !== 'sold' || isArchived || !estateId}
                              value={x.proceedsReceived}
                              onChange={(ev) => patch(r.key, r.requiresCourt, { proceedsReceived: ev.target.value })}
                            />
                          </td>
                          <td className="py-2 px-2 hidden xl:table-cell align-top">
                            <select
                              className="text-[11px] border border-stone-200 rounded px-1 py-1 bg-white disabled:opacity-40"
                              disabled={!r.requiresCourt || isArchived || !estateId}
                              value={
                                !r.requiresCourt
                                  ? 'not_required'
                                  : x.courtApprovalStatus === 'approved'
                                    ? 'approved'
                                    : x.courtApprovalStatus === 'pending'
                                      ? 'pending'
                                      : 'not_required'
                              }
                              onChange={(ev) =>
                                patch(r.key, r.requiresCourt, {
                                  courtApprovalStatus:
                                    ev.target.value === 'pending' || ev.target.value === 'approved'
                                      ? ev.target.value
                                      : 'not_required',
                                })
                              }
                            >
                              <option value="not_required">Not required</option>
                              <option value="pending">Pending</option>
                              <option value="approved">Approved</option>
                            </select>
                          </td>
                          <td className="py-2 px-2 align-top">
                            <textarea
                              className="w-[140px] rounded border px-2 py-1 text-xs min-h-[36px]"
                              disabled={isArchived || !estateId}
                              value={x.notes}
                              onChange={(ev) => patch(r.key, r.requiresCourt, { notes: ev.target.value })}
                            />
                            {x.status === 'blocked' ? (
                              <textarea
                                className="mt-1 w-[140px] rounded border border-red-100 px-2 py-1 text-xs min-h-[32px]"
                                disabled={isArchived || !estateId}
                                placeholder="Blocker"
                                value={x.blockerReason}
                                onChange={(ev) => patch(r.key, r.requiresCourt, { blockerReason: ev.target.value })}
                              />
                            ) : null}
                          </td>
                          <td className="py-2 px-2 align-top">
                            <RowDocumentPanel estateId={estateId} phase="distribute" stepId={`exec-240:${r.key}`} readOnly={isArchived} />
                          </td>
                        </tr>
                      </Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className="fixed inset-x-0 bottom-0 z-30 border-t border-stone-200 bg-white px-4 py-3 shadow-[0_-8px_20px_rgb(28_25_23/14%)]">
          <div className="mx-auto flex max-w-6xl flex-col gap-3 md:flex-row md:items-center md:justify-between text-sm">
            <div className="space-y-1 text-stone-800">
              <p>
                <span className="font-semibold text-blue-950">Total appraised value:</span> {moneyFmt(totals.appraise)}
              </p>
              <p>
                <span className="font-semibold text-blue-950">Total proceeds received:</span> {moneyFmt(totals.proceeds)}
              </p>
              <p>
                <span className="font-semibold text-blue-950">Assets awaiting action:</span> {totals.awaiting}
              </p>
            </div>
            <div className="flex flex-wrap gap-2">
              <Link to={ei ? `/sframe-220/${ei}` : '/sframe-220'} className="rounded-lg border border-stone-300 px-4 py-2 text-sm font-semibold">
                Save &amp; Continue Later
              </Link>
              {everyTransferred && ei ? (
                <Link className="rounded-lg bg-emerald-700 px-5 py-2 text-sm font-semibold text-white" to={`/sframe-250/${ei}`}>
                  Next: Complete Estate Documentation →
                </Link>
              ) : null}
            </div>
          </div>
        </div>
      </>
    </SettlementStepPage>
  );
}
